import { useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { formatNoteDate, MAX_SITE_NOTE_LENGTH } from '../../domain/siteNote';
import { useAddSiteNote, useSiteNotes } from './site-status-data';

/**
 * The site's status log (tidaltech/koi#8261): what Customer Success and
 * Field Services know about the site, newest first. "Contact with the
 * site" notes set the site's last contact.
 */
export function SiteStatusLogTab({ customerId, siteId }: { customerId: string; siteId: string }) {
  const { canMutate } = useAuth();
  const notes = useSiteNotes(customerId, siteId);
  const add = useAddSiteNote(customerId, siteId);
  const [text, setText] = useState('');
  const [contact, setContact] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    if (!text.trim()) {
      setError('Write the note first.');
      return;
    }
    add.mutate(
      { kind: contact ? 'CONTACT' : 'NOTE', text },
      {
        onSuccess: () => {
          setText('');
          setContact(false);
        },
        onError: () => setError('The note could not be saved.'),
      },
    );
  }

  const all = notes.data?.pages.flatMap((page) => page.notes) ?? [];
  return (
    <section aria-label="Status log" className="site-status-log">
      <form className="site-note-form" onSubmit={handleSubmit} aria-label="Add a status note">
        <label>
          New note
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={3}
            maxLength={MAX_SITE_NOTE_LENGTH}
            placeholder="e.g. Slaughtering M4 and M5 next week; wants equipment returned after"
            disabled={!canMutate}
          />
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={contact}
            onChange={(event) => setContact(event.target.checked)}
            disabled={!canMutate}
          />
          <span>This was contact with the site (call, email or visit)</span>
        </label>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}
        <button type="submit" className="primary" disabled={!canMutate || add.isPending}>
          {add.isPending ? 'Saving…' : 'Add note'}
        </button>
      </form>

      {notes.isLoading ? <PageLoadingState label="Loading notes…" /> : null}
      {notes.isError ? (
        <ErrorState
          title="Notes could not be loaded"
          rpcName="ListSiteNotes"
          onRetry={() => void notes.refetch()}
        />
      ) : null}
      {notes.isSuccess && all.length === 0 ? <p className="empty-note">No notes yet.</p> : null}
      <ol className="site-note-list">
        {all.map((note) => (
          <li key={note.id}>
            <div className="site-note-meta">
              {formatNoteDate(note.createTime)} · {note.author}
              {note.kind === 'CONTACT' ? <span className="state-badge">Contact</span> : null}
            </div>
            <p>{note.text}</p>
          </li>
        ))}
      </ol>
      {notes.hasNextPage ? (
        <button
          type="button"
          onClick={() => void notes.fetchNextPage()}
          disabled={notes.isFetchingNextPage}
        >
          {notes.isFetchingNextPage ? 'Loading…' : 'Older notes'}
        </button>
      ) : null}
    </section>
  );
}
